import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, ExternalLink, BookOpen, Info, ChevronRight, AlertTriangle, CheckCircle2, Loader2, Lightbulb, Scale, Clock, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature to Policy Wording mapping with page references
export const FEATURE_POLICY_MAPPING = {
  // Room & Accommodation
  room_rent: {
    title: "Room Rent / Accommodation",
    documentType: "policy_wording",
    section: "Coverage Benefits",
    keywords: ["room rent", "accommodation", "single private", "twin sharing", "general ward"],
    fallbackPage: "Section 2 - Coverage"
  },
  icu_limit: {
    title: "ICU / ICCU Charges",
    documentType: "policy_wording",
    section: "Coverage Benefits",
    keywords: ["icu", "iccu", "intensive care"],
    fallbackPage: "Section 2 - Coverage"
  },
  pre_hospitalization: {
    title: "Pre-Hospitalization Expenses",
    documentType: "policy_wording",
    section: "Pre-Hospitalization",
    keywords: ["pre-hospitalization", "60 days", "90 days"],
    fallbackPage: "Section 2 - Coverage"
  },
  post_hospitalization: {
    title: "Post-Hospitalization Expenses",
    documentType: "policy_wording",
    section: "Post-Hospitalization",
    keywords: ["post-hospitalization", "180 days", "90 days"],
    fallbackPage: "Section 2 - Coverage"
  },
  day_care: {
    title: "Day Care Procedures",
    documentType: "policy_wording",
    section: "Day Care Treatment",
    keywords: ["day care", "daycare", "less than 24 hours"],
    fallbackPage: "Section 2 - Coverage"
  },
  domiciliary: {
    title: "Domiciliary Hospitalization",
    documentType: "policy_wording",
    section: "Domiciliary",
    keywords: ["domiciliary", "home treatment"],
    fallbackPage: "Section 2 - Coverage"
  },
  road_ambulance: {
    title: "Road Ambulance",
    documentType: "policy_wording",
    section: "Ambulance Cover",
    keywords: ["ambulance", "road ambulance"],
    fallbackPage: "Section 2 - Coverage"
  },
  air_ambulance: {
    title: "Air Ambulance",
    documentType: "policy_wording",
    section: "Air Ambulance",
    keywords: ["air ambulance", "medical evacuation"],
    fallbackPage: "Section 2 - Coverage"
  },
  restore_benefit: {
    title: "Restoration / Recharge Benefit",
    documentType: "policy_wording",
    section: "Restoration Benefit",
    keywords: ["restoration", "recharge", "refill", "unlimited restoration"],
    fallbackPage: "Section 3 - Special Benefits"
  },
  ncb: {
    title: "No Claim Bonus / Cumulative Bonus",
    documentType: "policy_wording",
    section: "No Claim Bonus",
    keywords: ["no claim bonus", "ncb", "cumulative bonus"],
    fallbackPage: "Section 3 - Special Benefits"
  },
  initial_waiting: {
    title: "Initial Waiting Period",
    documentType: "policy_wording",
    section: "Waiting Periods",
    keywords: ["initial waiting", "30 days", "cooling period"],
    fallbackPage: "Section 5 - Waiting Periods"
  },
  pre_existing_waiting: {
    title: "Pre-Existing Disease Waiting Period",
    documentType: "policy_wording",
    section: "Pre-Existing Diseases",
    keywords: ["pre-existing", "ped", "36 months", "48 months"],
    fallbackPage: "Section 5 - Waiting Periods"
  },
  specific_disease_waiting: {
    title: "Specific Disease Waiting Period",
    documentType: "waiting_period",
    section: "Specific Disease Waiting",
    keywords: ["specific disease", "24 months", "cataract", "hernia"],
    fallbackPage: "Section 5 - Waiting Periods"
  },
  ayush_coverage: {
    title: "AYUSH Treatment",
    documentType: "policy_wording",
    section: "AYUSH",
    keywords: ["ayush", "ayurveda", "homeopathy"],
    fallbackPage: "Section 2 - Coverage"
  },
  maternity: {
    title: "Maternity Coverage",
    documentType: "policy_wording",
    section: "Maternity Benefit",
    keywords: ["maternity", "childbirth", "pregnancy"],
    fallbackPage: "Section 4 - Optional Covers"
  },
  opd: {
    title: "OPD / Out-Patient Coverage",
    documentType: "policy_wording",
    section: "OPD Cover",
    keywords: ["opd", "out-patient", "consultation"],
    fallbackPage: "Section 4 - Optional Covers"
  },
  critical_illness: {
    title: "Critical Illness Cover",
    documentType: "policy_wording",
    section: "Critical Illness",
    keywords: ["critical illness", "cancer", "heart attack"],
    fallbackPage: "Section 4 - Optional Covers"
  },
  hospital_cash: {
    title: "Hospital Daily Cash",
    documentType: "policy_wording",
    section: "Hospital Cash",
    keywords: ["hospital cash", "daily cash"],
    fallbackPage: "Section 4 - Optional Covers"
  },
  copay: {
    title: "Co-payment",
    documentType: "policy_wording",
    section: "Co-payment",
    keywords: ["co-pay", "copay"],
    fallbackPage: "Section 6 - Terms & Conditions"
  },
  sub_limits: {
    title: "Sub-Limits",
    documentType: "policy_wording",
    section: "Sub-Limits",
    keywords: ["sub-limit", "capping"],
    fallbackPage: "Section 6 - Terms & Conditions"
  },
  consumables: {
    title: "Consumables Coverage",
    documentType: "policy_wording",
    section: "Consumables",
    keywords: ["consumables", "non-medical items"],
    fallbackPage: "Section 2 - Coverage"
  },
  organ_donor: {
    title: "Organ Donor Expenses",
    documentType: "policy_wording",
    section: "Organ Donor",
    keywords: ["organ donor", "transplant"],
    fallbackPage: "Section 2 - Coverage"
  },
  reasonable_customary_charges: {
    title: "Reasonable & Customary Charges (R&C)",
    documentType: "policy_wording",
    section: "Definitions / Terms & Conditions",
    keywords: ["reasonable and customary", "R&C", "prevailing charges", "standard charges", "geographical area"],
    fallbackPage: "Section 1 - Definitions"
  },
  health_checkup: {
    title: "Annual Health Checkup",
    documentType: "health_checkup",
    section: "Health Checkup",
    keywords: ["health checkup", "preventive"],
    fallbackPage: "Section 3 - Special Benefits"
  },
  multi_year_discount: {
    title: "Multi-Year Discount",
    documentType: "brochure",
    section: "Discounts",
    keywords: ["multi-year", "long term"],
    fallbackPage: "Premium Section"
  },
  family_discount: {
    title: "Family Discount",
    documentType: "brochure",
    section: "Discounts",
    keywords: ["family discount"],
    fallbackPage: "Premium Section"
  },
  robotics: {
    title: "Modern Treatments",
    documentType: "policy_wording",
    section: "Modern Treatments",
    keywords: ["robotic", "stem cell", "gene therapy"],
    fallbackPage: "Section 2 - Coverage"
  }
};

// Rider to Policy Wording mapping
export const RIDER_POLICY_MAPPING = {
  maternity: { title: "Maternity Rider", documentType: "policy_wording", section: "Optional Covers - Maternity", fallbackPage: "Section 4 - Riders" },
  critical_illness: { title: "Critical Illness Rider", documentType: "policy_wording", section: "Optional Covers - Critical Illness", fallbackPage: "Section 4 - Riders" },
  personal_accident: { title: "Personal Accident Rider", documentType: "policy_wording", section: "Optional Covers - Personal Accident", fallbackPage: "Section 4 - Riders" },
  hospital_cash: { title: "Hospital Cash Rider", documentType: "policy_wording", section: "Optional Covers - Hospital Cash", fallbackPage: "Section 4 - Riders" },
  opd: { title: "OPD Cover Rider", documentType: "policy_wording", section: "Optional Covers - OPD", fallbackPage: "Section 4 - Riders" },
  room_upgrade: { title: "Room Rent Waiver Rider", documentType: "policy_wording", section: "Optional Covers - Room Rent", fallbackPage: "Section 4 - Riders" },
  ped_waiver: { title: "PED Waiting Period Waiver", documentType: "policy_wording", section: "Optional Covers - PED Waiver", fallbackPage: "Section 4 - Riders" },
  consumables: { title: "Consumables Cover Rider", documentType: "policy_wording", section: "Optional Covers - Consumables", fallbackPage: "Section 4 - Riders" },
  instant_cover: { title: "Instant Cover Rider", documentType: "policy_wording", section: "Optional Covers - Instant Cover", fallbackPage: "Section 4 - Riders" },
  unlimited_restore: { title: "Unlimited Restoration Rider", documentType: "policy_wording", section: "Optional Covers - Restoration", fallbackPage: "Section 4 - Riders" },
  ncb_super: { title: "Super No Claim Bonus Rider", documentType: "policy_wording", section: "Optional Covers - NCB", fallbackPage: "Section 4 - Riders" },
  air_ambulance: { title: "Air Ambulance Rider", documentType: "policy_wording", section: "Optional Covers - Air Ambulance", fallbackPage: "Section 4 - Riders" }
};

// Helper function to find the best matching document
export const findPolicyDocument = (documents, documentType) => {
  if (!documents || !Array.isArray(documents) || documents.length === 0) return null;
  
  const typeMapping = {
    policy_wording: ["policy_wording", "policy", "terms", "t&c"],
    brochure: ["brochure", "product"],
    waiting_period: ["waiting_period", "waiting", "specific"],
    health_checkup: ["health_checkup", "checkup", "health"],
    prospectus: ["prospectus"],
    cis: ["cis", "customer information"]
  };
  
  const searchTerms = typeMapping[documentType] || [documentType];
  
  let doc = documents.find(d => 
    d && d.type && searchTerms.some(term => d.type.toLowerCase().includes(term))
  );
  
  if (!doc) {
    doc = documents.find(d => 
      d && d.name && searchTerms.some(term => d.name.toLowerCase().includes(term))
    );
  }
  
  if (!doc) {
    doc = documents.find(d => 
      d && d.name && (d.name.toLowerCase().includes('policy') || d.name.toLowerCase().includes('terms'))
    );
  }
  
  if (!doc && documents.length > 0) {
    doc = documents[0];
  }
  
  return doc;
};

const PolicyWordingModal = ({ 
  isOpen, 
  onClose, 
  featureKey, 
  featureValue,
  planId,
  planName,
  planDocuments,
  customTitle,
  customDescription,
  isRider = false
}) => {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mapping = isRider ? RIDER_POLICY_MAPPING : FEATURE_POLICY_MAPPING;
  const featureInfo = mapping[featureKey] || {
    title: customTitle || featureKey?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    documentType: "policy_wording",
    section: "Coverage",
    fallbackPage: "Refer to Policy Document"
  };
  
  const policyDocument = findPolicyDocument(planDocuments, featureInfo.documentType);
  
  // Fetch explanation when modal opens
  useEffect(() => {
    if (isOpen && planId && featureKey) {
      fetchExplanation();
    }
  }, [isOpen, planId, featureKey]);
  
  const fetchExplanation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/plans/explain-feature`, {
        plan_id: planId,
        feature_key: featureKey,
        feature_value: featureValue,
        is_rider: isRider,
        custom_title: customTitle
      });
      setExplanation(response.data);
    } catch (err) {
      console.error("Error fetching explanation:", err);
      setError("Could not load explanation");
      // Use local fallback
      setExplanation({
        what_it_means: customDescription || featureInfo.description || "This feature provides coverage as per your policy terms.",
        key_conditions: [],
        important_ifs_buts: [],
        claim_impact: ""
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Render modal using portal to ensure it appears above everything
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Full Screen */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
            className="bg-black/80 backdrop-blur-md"
          />
          
          {/* Modal Container - Full Screen Centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}
            className="flex items-center justify-center p-4 overflow-y-auto"
          >
            <div className="glass-card border border-amber-500/30 overflow-hidden max-h-[90vh] w-full max-w-2xl flex flex-col my-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500/20 to-blue-500/20 p-4 border-b border-[#C9D4E6] flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-[#D4A017]/20 flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-[#A87608]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1B2A4A] text-lg">{explanation?.title || featureInfo.title}</h3>
                      <p className="text-sm text-gray-700">{planName}</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="h-8 w-8 rounded-lg bg-[#EDF1F8] hover:bg-white/20 flex items-center justify-center transition-colors"
                  >
                    <X className="h-4 w-4 text-gray-700" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5 space-y-5 overflow-y-auto flex-grow">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
                    <span className="ml-3 text-gray-700">Loading explanation...</span>
                  </div>
                ) : (
                  <>
                    {/* Current Value */}
                    {featureValue && (
                      <div className="bg-blue-900/30 rounded-xl p-4 border border-blue-500/20">
                        <p className="text-xs text-gray-700 uppercase tracking-wider mb-1">Your Plan's Value</p>
                        <p className="text-[#1B2A4A] font-bold text-lg">{featureValue}</p>
                      </div>
                    )}
                    
                    {/* What It Means - Simple Explanation */}
                    {explanation?.what_it_means && (
                      <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl p-4 border border-emerald-500/20">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-emerald-600 mb-1">What This Means (Simple Terms)</p>
                            <p className="text-[#2C3E60] text-sm leading-relaxed">{explanation.what_it_means}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Key Conditions */}
                    {explanation?.key_conditions && explanation.key_conditions.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]">
                          <Scale className="h-4 w-4 text-blue-600" />
                          <span>Key Conditions</span>
                        </div>
                        <div className="bg-blue-900/20 rounded-xl p-4 border border-blue-500/20">
                          <ul className="space-y-2">
                            {explanation.key_conditions.map((condition, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-[#2E3B57]">
                                <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <span>{condition}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    
                    {/* Important Ifs & Buts */}
                    {explanation?.important_ifs_buts && explanation.important_ifs_buts.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]">
                          <AlertTriangle className="h-4 w-4 text-[#A87608]" />
                          <span>Important Conditions & Exceptions</span>
                        </div>
                        <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-amber-500/20">
                          <ul className="space-y-2">
                            {explanation.important_ifs_buts.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <span className={`${item.startsWith('✅') ? 'text-emerald-600' : 'text-amber-700'}`}>
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    
                    {/* Claim Impact */}
                    {explanation?.claim_impact && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]">
                          <HelpCircle className="h-4 w-4 text-purple-600" />
                          <span>How It Affects Your Claim</span>
                        </div>
                        <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                          <p className="text-sm text-[#2C3E60] leading-relaxed">{explanation.claim_impact}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Plan Specific Details */}
                    {explanation?.plan_specific_details && (
                      <div className="bg-[#EDF1F8]/70 rounded-xl p-4 border border-[#C9D4E6]">
                        <p className="text-xs text-gray-700 uppercase tracking-wider mb-2">Plan-Specific Details</p>
                        <p className="text-sm text-[#2E3B57] whitespace-pre-line">{explanation.plan_specific_details}</p>
                      </div>
                    )}
                    
                    {/* Section Reference */}
                    <div className="flex items-center gap-2 text-sm">
                      <Info className="h-4 w-4 text-[#A87608]" />
                      <span className="text-gray-700">Document Reference:</span>
                      <span className="text-[#A87608] font-medium">{featureInfo.section}</span>
                    </div>
                    
                    {/* Document Link */}
                    {policyDocument ? (
                      <a
                        href={policyDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/10 rounded-xl border border-amber-500/30 hover:border-amber-500/50 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-[#D4A017]/20 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-[#A87608]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#1B2A4A] group-hover:text-[#A87608] transition-colors">
                              📄 View Full Policy Document
                            </p>
                            <p className="text-xs text-gray-700">
                              {policyDocument.name} • {featureInfo.fallbackPage}
                            </p>
                          </div>
                        </div>
                        <ExternalLink className="h-5 w-5 text-[#A87608] group-hover:translate-x-1 transition-transform" />
                      </a>
                    ) : (
                      <div className="p-4 bg-[#EDF1F8]/70 rounded-xl border border-[#C9D4E6]">
                        <p className="text-gray-700 text-sm">
                          Policy document not available. Please refer to the plan's Documents section.
                        </p>
                      </div>
                    )}
                    
                    {/* Other Documents */}
                    {planDocuments && planDocuments.length > 1 && (
                      <div className="pt-2 border-t border-[#C9D4E6]">
                        <p className="text-xs text-gray-700 mb-2">Other Available Documents:</p>
                        <div className="space-y-2">
                          {planDocuments.filter(d => d?.url !== policyDocument?.url).slice(0, 3).map((doc, idx) => (
                            <a
                              key={idx}
                              href={doc?.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#A87608] transition-colors"
                            >
                              <ChevronRight className="h-3 w-3" />
                              {doc?.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-[#C9D4E6] bg-blue-900/20 flex-shrink-0">
                <Button
                  onClick={onClose}
                  className="w-full btn-primary"
                >
                  Got It
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
  
  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default PolicyWordingModal;
